<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'createAssistant']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { assistants, rememberAssistant, canCreateAssistant } =
  useArrowsAiAssistants();

const currentAssistantId = computed(() => Number(route.params.assistantId));

const isActive = assistant => assistant.id === currentAssistantId.value;

const assistantRoutes = [
  'arrows_ai_assistants_overview',
  'arrows_ai_assistants_instructions',
  'arrows_ai_assistants_faqs',
  'arrows_ai_assistants_documents',
  'arrows_ai_assistants_resources',
  'arrows_ai_assistants_services',
  'arrows_ai_assistants_tools',
  'arrows_ai_assistants_playground',
  'arrows_ai_assistants_inboxes',
  'arrows_ai_assistants_settings',
  'arrows_ai_assistants_settings_system',
  'arrows_ai_assistants_settings_schedule',
  'arrows_ai_assistants_settings_limits',
  'arrows_ai_assistants_settings_reply_style',
];

const handleSelect = assistant => {
  if (isActive(assistant)) {
    emit('close');
    return;
  }

  rememberAssistant(assistant.id);
  const name = assistantRoutes.includes(route.name)
    ? route.name
    : 'arrows_ai_assistants_overview';

  router.push({
    name,
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });
  emit('close');
};
</script>

<template>
  <div
    class="absolute z-50 flex flex-col w-[27.5rem] max-h-96 gap-4 pt-5 rounded-xl shadow-md outline outline-1 outline-n-container bg-n-alpha-3 backdrop-blur-[100px]"
  >
    <div
      class="flex items-center justify-between gap-4 px-6 pb-3 border-b border-n-alpha-2"
    >
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('ARROWS_AI.SWITCHER.ASSISTANTS') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.SWITCHER.SWITCH') }}
        </p>
      </div>
      <Button
        :label="t('ARROWS_AI.SWITCHER.NEW')"
        color="slate"
        icon="i-lucide-plus"
        size="sm"
        :disabled="!canCreateAssistant"
        class="!bg-n-alpha-2 hover:!bg-n-alpha-3"
        @click="emit('createAssistant')"
      />
    </div>
    <div
      v-if="assistants.length"
      class="flex flex-col flex-1 min-h-0 gap-2 px-4 pb-3 overflow-y-auto"
    >
      <Button
        v-for="assistant in assistants"
        :key="assistant.id"
        variant="ghost"
        color="slate"
        size="sm"
        trailing-icon
        :icon="isActive(assistant) ? 'i-lucide-check' : ''"
        class="!justify-end !px-2 !py-2 h-9 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10"
        @click="handleSelect(assistant)"
      >
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ assistant.name }}
        </span>
        <Avatar
          :name="assistant.name"
          :size="20"
          icon-name="i-lucide-bot"
          rounded-full
        />
      </Button>
    </div>
    <p v-else class="px-4 py-3 text-sm text-n-slate-11">
      {{ t('ARROWS_AI.SWITCHER.EMPTY') }}
    </p>
  </div>
</template>
